import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Apple, Smartphone, CheckCircle2, XCircle, Copy, ExternalLink, DollarSign, Eye, TrendingUp, MousePointerClick, RefreshCw, Download, GitCompare } from 'lucide-react';
import { appsService } from '../services/appsService';
import { analyticsService } from '../services/analyticsService';
import { getDefaultDateRange } from '../utils/config';
import SummaryCard from '../components/dashboard/SummaryCard';
import RevenueChart from '../components/dashboard/RevenueChart';
import DateRangePicker from '../components/dashboard/DateRangePicker';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

/**
 * App Details Page
 * Displays comprehensive app information, ad units, networks, and performance
 */
const AppDetails = () => {
  const { appKey } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [appDetails, setAppDetails] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [accountId, setAccountId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Date range state (defaults to current month, 1st to today)
  const [dateRange, setDateRange] = useState(() => getDefaultDateRange());

  // Fetch app details
  const fetchAppDetails = async (showRefreshing = false) => {
    if (!appKey) return;

    try {
      if (showRefreshing) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const params = {
        start_date: dateRange.startDate,
        end_date: dateRange.endDate,
      };

      if (accountId) {
        params.account_id = accountId;
      }

      const [detailsResponse, chartResponse] = await Promise.all([
        appsService.getAppDetails(appKey, params),
        analyticsService.getRevenueChart({
          ...params,
          appKey: appKey, // Filter chart data by this app
          period: 'daily'
        }).catch(() => ({ data: { data: [] } })) // Fallback to empty data if chart fails
      ]);

      setAppDetails(detailsResponse.data);
      setChartData(chartResponse.data);
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Failed to load app details';
      setError(errorMessage);
      console.error('App details fetch error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchAppDetails(true);
  };

  const handleExportData = () => {
    // TODO: Implement export functionality when export APIs are available
    // For now, show a message or prepare data for export
    const exportData = {
      app: appDetails?.app,
      performance: appDetails?.performance,
      ad_units: appDetails?.ad_units,
      networks: appDetails?.networks,
      exported_at: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `app-${appKey}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    fetchAppDetails();
  }, [appKey, dateRange.startDate, dateRange.endDate, accountId]);

  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Could add a toast notification here
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading && !appDetails) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading app details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive mb-4">{error}</p>
            <div className="flex gap-2">
              <Button onClick={() => navigate('/apps')} variant="outline">
                Back to Apps
              </Button>
              <Button onClick={fetchAppDetails} variant="destructive">
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!appDetails) {
    return null;
  }

  const app = appDetails.app || {};
  const adUnits = appDetails.ad_units || [];
  const networks = appDetails.networks || {};
  const performance = appDetails.performance || {};

  const adUnitNames = {
    rewardedVideo: 'Rewarded Video',
    interstitial: 'Interstitial',
    banner: 'Banner',
    offerWall: 'Offer Wall',
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
      {/* Header */}
      <div className="mb-2 sm:mb-3 lg:mb-4">
        <Button
          onClick={() => navigate('/apps')}
          variant="ghost"
          size="sm"
          className="mb-2 text-xs"
        >
          <ArrowLeft className="w-3 h-3 mr-1.5" />
          Back to Apps
        </Button>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
              {app.platform === 'iOS' ? (
                <Apple className="w-6 h-6 sm:w-10 sm:h-10" />
              ) : (
                <Smartphone className="w-6 h-6 sm:w-10 sm:h-10" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold mb-0.5 break-words">{app.app_name || app.app_key}</h1>
              <div className="flex items-center gap-3">
                <Badge variant={app.platform === 'iOS' ? 'default' : 'secondary'}>
                  {app.platform === 'iOS' ? (
                    <><Apple className="w-3 h-3 mr-1" /> iOS</>
                  ) : (
                    <><Smartphone className="w-3 h-3 mr-1" /> Android</>
                  )}
                </Badge>
                <Badge variant={app.status === 'active' ? 'default' : 'secondary'}>
                  {app.status === 'active' ? (
                    <><CheckCircle2 className="w-3 h-3 mr-1" /> Active</>
                  ) : (
                    <><XCircle className="w-3 h-3 mr-1" /> Inactive</>
                  )}
                </Badge>
                {app.last_sync && (() => {
                  const syncDate = new Date(app.last_sync);
                  const now = new Date();
                  const diffMs = now - syncDate;
                  const diffMins = Math.floor(diffMs / 60000);

                  let syncStatusColor = 'bg-green-600';
                  if (diffMins >= 60) syncStatusColor = 'bg-yellow-600';
                  if (diffMins >= 1440) syncStatusColor = 'bg-red-600';

                  return (
                    <div className={`w-2 h-2 rounded-full ${syncStatusColor}`} title={`Last synced: ${diffMins < 60 ? `${diffMins}m ago` : diffMins < 1440 ? `${Math.floor(diffMins / 60)}h ago` : `${Math.floor(diffMins / 1440)}d ago`}`} />
                  );
                })()}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleRefresh}
              disabled={refreshing}
              variant="outline"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button
              onClick={handleExportData}
              variant="outline"
              disabled={!appDetails}
            >
              <Download className="w-4 h-4 mr-2" />
              Export Data
            </Button>
            <Button
              asChild
              variant="outline"
            >
              <Link to={`/analytics?appKey=${app.app_key}`}>
                <GitCompare className="w-4 h-4 mr-2" />
                Compare
              </Link>
            </Button>
            <Button
              asChild
              variant="default"
            >
              <Link to={`/analytics?appKey=${app.app_key}`}>
                <ExternalLink className="w-4 h-4 mr-2" />
                View Analytics
              </Link>
            </Button>
          </div>
        </div>
      </div>

      {/* App Information */}
      <Card className="mb-2 sm:mb-3 lg:mb-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm sm:text-base">App Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3 lg:gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">App Key</label>
              <div className="flex items-center gap-2 mt-1">
                <code className="text-sm bg-muted px-2 py-1 rounded">{app.app_key}</code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(app.app_key)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            {app.bundle_id && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Bundle ID</label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="text-sm bg-muted px-2 py-1 rounded">{app.bundle_id}</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(app.bundle_id)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-muted-foreground">Creation Date</label>
              <p className="text-sm mt-1">{formatDate(app.creation_date)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Last Sync</label>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-sm">
                  {app.last_sync ? formatDate(app.last_sync) : 'Never'}
                </p>
                {app.last_sync && (() => {
                  const syncDate = new Date(app.last_sync);
                  const now = new Date();
                  const diffMs = now - syncDate;
                  const diffMins = Math.floor(diffMs / 60000);

                  let syncStatusColor = 'bg-green-600';
                  let syncStatusText = 'Recently synced';
                  if (diffMins >= 60) {
                    syncStatusColor = 'bg-yellow-600';
                    syncStatusText = 'Sync needed';
                  }
                  if (diffMins >= 1440) {
                    syncStatusColor = 'bg-red-600';
                    syncStatusText = 'Sync overdue';
                  }

                  return (
                    <div className="flex items-center gap-1">
                      <div className={`w-2 h-2 rounded-full ${syncStatusColor}`} title={syncStatusText} />
                      <span className="text-xs text-muted-foreground">
                        {diffMins < 60 ? `${diffMins}m ago` : diffMins < 1440 ? `${Math.floor(diffMins / 60)}h ago` : `${Math.floor(diffMins / 1440)}d ago`}
                      </span>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Date Range Picker */}
      <Card className="mb-2 sm:mb-3 lg:mb-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm sm:text-base">Performance Period</CardTitle>
        </CardHeader>
        <CardContent>
          <DateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={handleDateRangeChange}
          />
        </CardContent>
      </Card>

      {/* Performance Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4 mb-2 sm:mb-3 lg:mb-4">
        <SummaryCard
          title="Total Revenue"
          value={performance.total_revenue || 0}
          subtitle={`${performance.date_range?.start_date || dateRange.startDate} - ${performance.date_range?.end_date || dateRange.endDate}`}
          icon={DollarSign}
          isCurrency={true}
        />
        <SummaryCard
          title="Total Impressions"
          value={performance.total_impressions || 0}
          subtitle={`${performance.active_days || 0} active days`}
          icon={Eye}
          isCurrency={false}
        />
        <SummaryCard
          title="Total Clicks"
          value={performance.total_clicks || 0}
          subtitle={`CTR: ${performance.ctr?.toFixed(2) || 0}%`}
          icon={MousePointerClick}
          isCurrency={false}
        />
        <SummaryCard
          title="Average eCPM"
          value={performance.average_ecpm || 0}
          subtitle={`${performance.active_ad_units || 0} active ad units`}
          icon={TrendingUp}
          isCurrency={true}
        />
      </div>

      {/* Revenue Chart */}
      <Card className="mb-2 sm:mb-3 lg:mb-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm sm:text-base">Revenue Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <RevenueChart
            data={chartData?.data || []}
            type="line"
            height={300}
            showLegend={true}
          />
        </CardContent>
      </Card>

      {/* Ad Units Breakdown */}
      <Card className="mb-2 sm:mb-3 lg:mb-4">
        <CardHeader>
          <CardTitle>Ad Units</CardTitle>
        </CardHeader>
        <CardContent>
          {adUnits.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ad Unit</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Revenue</TableHead>
                  <TableHead>Impressions</TableHead>
                  <TableHead>Clicks</TableHead>
                  <TableHead>eCPM</TableHead>
                  <TableHead>Active Days</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {adUnits.map((adUnit) => (
                  <TableRow key={adUnit.ad_unit}>
                    <TableCell className="font-medium">
                      {adUnit.ad_unit_name || adUnit.ad_unit}
                    </TableCell>
                    <TableCell>
                      <Badge variant={adUnit.status === 'active' ? 'default' : 'secondary'}>
                        {adUnit.status === 'active' ? (
                          <><CheckCircle2 className="w-3 h-3 mr-1" /> Active</>
                        ) : (
                          <><XCircle className="w-3 h-3 mr-1" /> Inactive</>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      ${(adUnit.total_revenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell>{(adUnit.total_impressions || 0).toLocaleString()}</TableCell>
                    <TableCell>{(adUnit.total_clicks || 0).toLocaleString()}</TableCell>
                    <TableCell>
                      ${(adUnit.average_ecpm || 0).toFixed(2)}
                    </TableCell>
                    <TableCell>{adUnit.active_days || 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-muted-foreground text-center py-8">No ad units data available</p>
          )}
        </CardContent>
      </Card>

      {/* Networks by Ad Unit */}
      {Object.keys(networks).length > 0 && (
        <div className="space-y-2 sm:space-y-3 lg:space-y-4 mb-2 sm:mb-3 lg:mb-4">
          {Object.entries(networks).map(([adUnit, networkList]) => (
            <Card key={adUnit}>
              <CardHeader>
                <CardTitle>
                  Networks - {adUnitNames[adUnit] || adUnit}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {networkList && networkList.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Network</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Revenue</TableHead>
                        <TableHead>Impressions</TableHead>
                        <TableHead>Active Days</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {networkList.map((network, index) => (
                        <TableRow key={`${network.network}-${index}`}>
                          <TableCell className="font-medium">{network.network}</TableCell>
                          <TableCell>
                            <Badge variant={network.status === 'active' ? 'default' : 'secondary'}>
                              {network.status === 'active' ? (
                                <><CheckCircle2 className="w-3 h-3 mr-1" /> Active</>
                              ) : (
                                <><XCircle className="w-3 h-3 mr-1" /> Inactive</>
                              )}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            ${(network.total_revenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell>{(network.total_impressions || 0).toLocaleString()}</TableCell>
                          <TableCell>{network.active_days || 0}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-muted-foreground text-center py-4">No networks data available</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AppDetails;
